import React, { useState, useEffect, useRef } from "react";
import {
  Shield, Terminal, AlertTriangle, CheckCircle2, XCircle,
  User, KeyRound, Boxes, Play, RotateCcw, ChevronRight, Fingerprint,
  Skull, Crown, DoorClosed, Bug,
} from "lucide-react";

/* ------------------------------------------------------------------ *
 *  MODULE: Security Context Constraints (SCC) — OpenShift
 *  Style: dark terminal, OpenShift-red signature. Linear scroll.
 * ------------------------------------------------------------------ */

const C = {
  bg: "#080b12",
  panel: "#0e141f",
  panelHi: "#131c2b",
  border: "#1e2a3d",
  borderHi: "#2e4059",
  text: "#e6edf3",
  muted: "#8b98a5",
  faint: "#586878",
  red: "#ff4d4d",
  redDeep: "#cc0000",
  green: "#3ddc97",
  amber: "#f5b942",
  cyan: "#4fc3f7",
  purple: "#c08fe0",
};

const mono = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace";
const sans = "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

/* ---------- tiny building blocks ---------- */

const Section = ({ id, kicker, title, children }) => (
  <section
    id={id}
    style={{
      borderTop: `1px solid ${C.border}`,
      padding: "56px 0",
    }}
  >
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 20px" }}>
      {kicker && (
        <div
          style={{
            fontFamily: mono,
            fontSize: 12,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: C.red,
            marginBottom: 10,
          }}
        >
          {kicker}
        </div>
      )}
      <h2
        style={{
          fontFamily: sans,
          fontSize: 30,
          fontWeight: 800,
          color: C.text,
          margin: "0 0 22px",
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>
      <div style={{ color: C.muted, fontFamily: sans, fontSize: 16, lineHeight: 1.75 }}>
        {children}
      </div>
    </div>
  </section>
);

const K = ({ children }) => (
  <span
    style={{
      fontFamily: mono,
      fontSize: "0.88em",
      color: C.cyan,
      background: "#0a1420",
      border: `1px solid ${C.border}`,
      borderRadius: 5,
      padding: "1px 6px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const Analogy = ({ icon, title, children }) => (
  <div
    style={{
      background: "linear-gradient(180deg,#160f0a,#0e0a08)",
      border: `1px solid ${C.amber}44`,
      borderLeft: `3px solid ${C.amber}`,
      borderRadius: 10,
      padding: "18px 20px",
      margin: "22px 0",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        color: C.amber,
        fontFamily: mono,
        fontSize: 12,
        letterSpacing: 2,
        textTransform: "uppercase",
        marginBottom: 10,
      }}
    >
      {icon} Analogy — {title}
    </div>
    <div style={{ color: "#e8ddc9", fontSize: 15.5, lineHeight: 1.7 }}>{children}</div>
  </div>
);

const Warn = ({ children }) => (
  <div
    style={{
      background: "#1a0b0b",
      border: `1px solid ${C.red}55`,
      borderLeft: `3px solid ${C.red}`,
      borderRadius: 10,
      padding: "16px 18px",
      margin: "20px 0",
      display: "flex",
      gap: 12,
    }}
  >
    <AlertTriangle size={20} color={C.red} style={{ flexShrink: 0, marginTop: 2 }} />
    <div style={{ color: "#f3cccc", fontSize: 15, lineHeight: 1.7 }}>{children}</div>
  </div>
);

const TerminalBlock = ({ lines }) => (
  <div
    style={{
      background: "#060a10",
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      overflow: "hidden",
      margin: "18px 0",
      fontFamily: mono,
      fontSize: 13,
    }}
  >
    <div
      style={{
        background: "#0c121b",
        borderBottom: `1px solid ${C.border}`,
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: 7,
      }}
    >
      <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ff5f56" }} />
      <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ffbd2e" }} />
      <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#27c93f" }} />
      <span style={{ color: C.faint, marginLeft: 8, fontSize: 12 }}>
        sa@lab.ocp.local:~
      </span>
    </div>
    <div style={{ padding: "14px 16px", lineHeight: 1.85 }}>
      {lines.map((l, i) => (
        <div key={i} style={{ color: l.c || C.text, whiteSpace: "pre-wrap" }}>
          {l.p && <span style={{ color: C.green }}>{l.p} </span>}
          {l.t}
        </div>
      ))}
    </div>
  </div>
);

/* ================================================================== *
 *  SIMULATOR — Admission Controller decision engine
 * ================================================================== */

// Simplified SCC capability model for teaching
const SCCS = {
  "restricted-v2": {
    color: C.green,
    root: false,     // must run non-root
    anyUid: false,   // uid forced from namespace range
    privileged: false,
    hostNet: false,
    note: "Cluster default. Sabse locked-down. Non-root forced, caps dropped.",
  },
  "nonroot-v2": {
    color: C.cyan,
    root: false,
    anyUid: true,    // any non-root uid the image wants
    privileged: false,
    hostNet: false,
    note: "Image apna non-root UID choose kar sakta hai. Root still blocked.",
  },
  anyuid: {
    color: C.amber,
    root: true,      // UID 0 allowed
    anyUid: true,
    privileged: false,
    hostNet: false,
    note: "UID 0 (root) allowed. Legacy images ke liye common. Risky.",
  },
  "hostnetwork-v2": {
    color: C.purple,
    root: false,
    anyUid: true,
    privileged: false,
    hostNet: true,   // host network allowed
    note: "Pod host ka network stack use kar sakta hai. Ingress/agents ke liye.",
  },
  privileged: {
    color: C.red,
    root: true,
    anyUid: true,
    privileged: true,
    hostNet: true,
    note: "ALL-ACCESS. Root on host possible. Sirf trusted system pods.",
  },
};

const STAGES = [
  { key: "submit", label: "oc apply", sub: "Pod request submitted", icon: Terminal },
  { key: "authn", label: "Authentication", sub: "Kaun ho tum? → ServiceAccount", icon: Fingerprint },
  { key: "authz", label: "Authorization", sub: "RBAC — create pod?", icon: KeyRound },
  { key: "scc", label: "SCC Admission", sub: "SecurityContext check", icon: Shield },
  { key: "verdict", label: "Verdict", sub: "Admit or reject", icon: DoorClosed },
];

function evaluate(pod, sccName) {
  const scc = SCCS[sccName];
  // check in order of severity, return first failure
  if (pod.privileged && !scc.privileged) {
    return {
      ok: false,
      reason: `Pod maang raha hai privileged: true, par "${sccName}" privileged containers allow nahi karta.`,
      field: "allowPrivilegedContainer: false",
    };
  }
  if (pod.hostNet && !scc.hostNet) {
    return {
      ok: false,
      reason: `Pod host network maang raha hai, par "${sccName}" hostNetwork allow nahi karta.`,
      field: "allowHostNetwork: false",
    };
  }
  if (pod.root && !scc.root) {
    return {
      ok: false,
      reason: `Pod runAsUser: 0 (root) maang raha hai, par "${sccName}" ko non-root chahiye.`,
      field: "runAsUser: MustRunAsRange / MustRunAsNonRoot",
    };
  }
  return { ok: true, reason: `"${sccName}" ne saari demands allow ki. Pod admitted → scheduled → Running.` };
}

const Toggle = ({ on, set, label, danger }) => (
  <button
    onClick={() => set(!on)}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      background: on ? (danger ? "#2a1010" : "#0f1a24") : "#0b1119",
      border: `1px solid ${on ? (danger ? C.red : C.cyan) : C.border}`,
      borderRadius: 8,
      padding: "10px 13px",
      cursor: "pointer",
      color: C.text,
      fontFamily: mono,
      fontSize: 13,
      transition: "all .15s",
    }}
  >
    <span>{label}</span>
    <span
      style={{
        width: 40,
        height: 22,
        borderRadius: 20,
        background: on ? (danger ? C.red : C.cyan) : "#243449",
        position: "relative",
        transition: "all .2s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "all .2s",
        }}
      />
    </span>
  </button>
);

function Simulator() {
  const [pod, setPod] = useState({ root: true, privileged: false, hostNet: false });
  const [sccName, setSccName] = useState("restricted-v2");
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState(-1);
  const [result, setResult] = useState(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const run = () => {
    clearTimers();
    setResult(null);
    setRunning(true);
    setActive(0);
    const res = evaluate(pod, sccName);
    // light up authn, authz, scc
    [1, 2, 3].forEach((i) =>
      timers.current.push(setTimeout(() => setActive(i), 650 * i))
    );
    // verdict
    timers.current.push(
      setTimeout(() => {
        setActive(4);
        setResult(res);
        setRunning(false);
      }, 650 * 4 + 250)
    );
  };

  const reset = () => {
    clearTimers();
    setRunning(false);
    setActive(-1);
    setResult(null);
  };

  const setP = (k) => (v) => setPod((p) => ({ ...p, [k]: v }));

  return (
    <div
      style={{
        background: C.panel,
        border: `1px solid ${C.borderHi}`,
        borderRadius: 14,
        padding: 20,
        margin: "10px 0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 18,
          fontFamily: mono,
          color: C.text,
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        <Shield size={18} color={C.red} /> SCC Admission Simulator
        <span style={{ color: C.faint, fontWeight: 400, fontSize: 12 }}>
          — pod ki demand vs SCC ki power
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* LEFT: pod wants */}
        <div>
          <div style={{ ...ctlHead, color: C.cyan }}>
            <Boxes size={14} /> POD KYA MAANG RAHA HAI
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Toggle on={pod.root} set={setP("root")} label="runAsUser: 0 (root)" danger />
            <Toggle on={pod.privileged} set={setP("privileged")} label="privileged: true" danger />
            <Toggle on={pod.hostNet} set={setP("hostNet")} label="hostNetwork: true" danger />
          </div>
        </div>

        {/* RIGHT: SCC on service account */}
        <div>
          <div style={{ ...ctlHead, color: C.amber }}>
            <KeyRound size={14} /> SERVICEACCOUNT KA SCC
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.keys(SCCS).map((n) => (
              <button
                key={n}
                onClick={() => setSccName(n)}
                style={{
                  textAlign: "left",
                  background: sccName === n ? "#0f1a26" : "#0b1119",
                  border: `1px solid ${sccName === n ? SCCS[n].color : C.border}`,
                  borderRadius: 8,
                  padding: "8px 11px",
                  cursor: "pointer",
                  color: C.text,
                  fontFamily: mono,
                  fontSize: 12.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: SCCS[n].color,
                    flexShrink: 0,
                  }}
                />
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: C.faint, fontFamily: mono, margin: "10px 2px 16px" }}>
        {SCCS[sccName].note}
      </div>

      {/* pipeline */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 6,
          background: "#060a10",
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: 12,
          overflowX: "auto",
        }}
      >
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const done = active > i || (active === i && i === 4 && result);
          const isActive = active === i;
          const isSccVerdict = i === 4 && result;
          const good = result?.ok;
          let ring = C.border;
          let ic = C.faint;
          if (isActive || done) {
            ring = C.cyan;
            ic = C.cyan;
          }
          if (isSccVerdict) {
            ring = good ? C.green : C.red;
            ic = good ? C.green : C.red;
          }
          return (
            <React.Fragment key={s.key}>
              <div
                style={{
                  flex: 1,
                  minWidth: 92,
                  borderRadius: 9,
                  border: `1px solid ${ring}`,
                  background: isActive ? "#0e1a26" : "#0a0f17",
                  padding: "11px 8px",
                  textAlign: "center",
                  transition: "all .3s",
                  boxShadow: isActive ? `0 0 0 3px ${ring}22` : "none",
                }}
              >
                <div className={isActive && running ? "scc-pulse" : ""} style={{ display: "inline-flex" }}>
                  {isSccVerdict ? (
                    good ? <CheckCircle2 size={22} color={C.green} /> : <XCircle size={22} color={C.red} />
                  ) : (
                    <Icon size={22} color={ic} />
                  )}
                </div>
                <div style={{ fontFamily: mono, fontSize: 11.5, color: C.text, marginTop: 6, fontWeight: 700 }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 10, color: C.faint, marginTop: 3, lineHeight: 1.3 }}>{s.sub}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ChevronRight size={16} color={active > i ? C.cyan : C.faint} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* verdict banner */}
      {result && (
        <div
          style={{
            marginTop: 14,
            background: result.ok ? "#0a1a12" : "#1a0b0b",
            border: `1px solid ${result.ok ? C.green : C.red}66`,
            borderRadius: 10,
            padding: "14px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              fontFamily: mono,
              fontWeight: 700,
              color: result.ok ? C.green : C.red,
              marginBottom: result.ok ? 0 : 6,
            }}
          >
            {result.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            {result.ok ? "ADMITTED — pod Running ✓" : "DENIED — pod create nahi hua ✗"}
          </div>
          <div style={{ color: result.ok ? "#bfe9d3" : "#f3cccc", fontSize: 13.5, lineHeight: 1.6 }}>
            {result.reason}
          </div>
          {!result.ok && (
            <div style={{ fontFamily: mono, fontSize: 12, color: C.amber, marginTop: 6 }}>
              blocked by → {result.field}
            </div>
          )}
        </div>
      )}

      {/* controls */}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button
          onClick={run}
          disabled={running}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            background: running ? "#22303f" : C.redDeep,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontFamily: mono,
            fontSize: 13,
            fontWeight: 700,
            cursor: running ? "default" : "pointer",
          }}
        >
          <Play size={15} /> {running ? "checking…" : "oc apply -f pod.yaml"}
        </button>
        <button
          onClick={reset}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            background: "transparent",
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "10px 16px",
            fontFamily: mono,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <RotateCcw size={14} /> reset
        </button>
      </div>
    </div>
  );
}

const ctlHead = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontFamily: mono,
  fontSize: 11,
  letterSpacing: 1.5,
  marginBottom: 10,
};

/* ---------- SCC spectrum bar ---------- */
function Spectrum() {
  const items = [
    { n: "restricted-v2", c: C.green, l: "Default • locked down" },
    { n: "nonroot-v2", c: C.cyan, l: "Any non-root UID" },
    { n: "anyuid", c: C.amber, l: "Root UID allowed" },
    { n: "hostnetwork-v2", c: C.purple, l: "Host network" },
    { n: "hostaccess", c: "#e07a5f", l: "Host dirs + net + IPC" },
    { n: "privileged", c: C.red, l: "All-access • root on host" },
  ];
  return (
    <div style={{ margin: "18px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: mono, fontSize: 11, color: C.faint, marginBottom: 8 }}>
        <span style={{ color: C.green }}>← SAFEST (kam power)</span>
        <span style={{ color: C.red }}>DANGEROUS (zyada power) →</span>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {items.map((it) => (
          <div key={it.n} style={{ flex: 1 }}>
            <div style={{ height: 8, background: it.c, borderRadius: 4, opacity: 0.85 }} />
            <div style={{ fontFamily: mono, fontSize: 10.5, color: C.text, marginTop: 7, fontWeight: 700 }}>{it.n}</div>
            <div style={{ fontSize: 10, color: C.faint, marginTop: 2 }}>{it.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- collapsible Q&A ---------- */
function QA({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          border: "none",
          color: C.text,
          fontFamily: sans,
          fontSize: 15,
          fontWeight: 600,
          padding: "14px 2px",
          cursor: "pointer",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        <ChevronRight
          size={17}
          color={C.red}
          style={{ marginTop: 3, flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}
        />
        {q}
      </button>
      {open && (
        <div style={{ color: C.muted, fontSize: 14.5, lineHeight: 1.7, padding: "0 2px 16px 27px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ================================================================== *
 *  PAGE
 * ================================================================== */
export default function OpenshiftSCC() {
  return (
    <div style={{ background: C.bg, color: C.text, minHeight: "100vh", fontFamily: sans }}>
      <style>{`
        @keyframes sccPulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.18);opacity:.7} }
        .scc-pulse { animation: sccPulse 1s ease-in-out infinite; }
        @keyframes blink { 50% { opacity: 0 } }
        .blink { animation: blink 1s step-end infinite; }
        * { box-sizing: border-box; }
      `}</style>

      {/* HERO */}
      <header style={{ borderBottom: `1px solid ${C.border}`, background: "linear-gradient(180deg,#0d0608,#080b12)" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "54px 20px 46px" }}>
          <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 3, color: C.red, marginBottom: 14 }}>
            OPENSHIFT · SECURITY MODULE
          </div>
          <h1 style={{ fontSize: 44, fontWeight: 900, margin: "0 0 14px", lineHeight: 1.08, letterSpacing: -1 }}>
            Security Context Constraints
            <span style={{ color: C.red }}> (SCC)</span>
          </h1>
          <p style={{ color: C.muted, fontSize: 17, lineHeight: 1.7, maxWidth: 640, margin: 0 }}>
            Har pod ke cluster me ghusne se pehle ek <b style={{ color: C.text }}>darban</b> khada hota hai jo poochta
            hai — <i>"tu kitni power maang raha hai, aur utni tujhe allowed hai kya?"</i> Woh darban hi SCC hai.
            Chalo bilkul zero se samajhte hain.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap", fontFamily: mono, fontSize: 12 }}>
            {["Kyun chahiye", "Kaise kaam karta", "Kaise manage", "Live use-case", "Cheat sheet"].map((t, i) => (
              <span key={t} style={{ border: `1px solid ${C.border}`, borderRadius: 20, padding: "6px 13px", color: C.muted }}>
                {String(i + 1).padStart(2, "0")} · {t}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* 1 — THE PROBLEM */}
      <Section id="why" kicker="01 · Root Cause" title="Pehle: dikkat kya hai?">
        <p>
          Ek container asal me host node par chal rahi ek <b style={{ color: C.text }}>process</b> hi hai — VM nahi,
          bas isolation ke saath ek process. Ab socho ye container <K>root</K> (UID 0) ke roop me chal raha hai.
          Agar container runtime ya kernel me koi <b style={{ color: C.text }}>container-escape vulnerability</b> nikal
          aaye, toh andar ka root bahar host ka root ban sakta hai.
        </p>
        <p>
          Aur host compromise hone ka matlab — <b style={{ color: C.red }}>us node ke saare pods</b>, chahe woh kisi bhi
          team ya customer ke ho, ek hi jhatke me gaye. Multi-tenant cluster (jaise tumhara <K>lab.ocp.local</K>) me ye
          disaster hai.
        </p>

        <div
          style={{
            display: "flex",
            gap: 14,
            margin: "22px 0",
            background: C.panel,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 18,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.muted, fontFamily: mono, fontSize: 13 }}>
            <Boxes size={20} color={C.cyan} /> root container
          </div>
          <ChevronRight size={16} color={C.faint} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.amber, fontFamily: mono, fontSize: 13 }}>
            <Bug size={20} color={C.amber} /> escape bug
          </div>
          <ChevronRight size={16} color={C.faint} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.red, fontFamily: mono, fontSize: 13 }}>
            <Skull size={20} color={C.red} /> host root → poora node gaya
          </div>
        </div>

        <p>
          Toh humein chahiye ek aisa system jo <b style={{ color: C.text }}>pod banne se pehle hi</b> decide kare ki
          "is pod ko itni power deni hai ya nahi." Yahi kaam <b style={{ color: C.red }}>SCC</b> karta hai — ye ek{" "}
          <b style={{ color: C.text }}>admission controller</b> hai jo har pod ke <K>securityContext</K> ko rules ke
          against jaanchta hai.
        </p>
        <p style={{ color: C.faint, fontSize: 14 }}>
          Note: plain Kubernetes me iska naya version <K>Pod Security Admission</K> hai. Par SCC OpenShift ka apna,
          purana aur zyada granular system hai — aur OpenShift me default se ON rehta hai. Yahi tumhe production me milega.
        </p>
      </Section>

      {/* 2 — THE ANALOGY / WHAT IS SCC */}
      <Section id="what" kicker="02 · Mental Model" title="SCC actually hai kya?">
        <p>
          SCC ek rulebook hai jo batata hai ki ek pod <b style={{ color: C.text }}>security ke level par kya-kya kar
          sakta hai</b>: root ban sakta hai kya? kaunse UID range me? privileged mode allowed? host ka network/disk
          touch kar sakta hai? kaunsi Linux capabilities (jaise <K>NET_ADMIN</K>) le sakta hai? Har cheez ka jawab SCC
          me likha hota hai.
        </p>

        <Analogy icon={<Crown size={15} />} title="Club ka Bouncer + Wristband">
          Ek nightclub imagine karo. Har guest (<b>= pod</b>) entry pe demand rakhta hai: <i>"mujhe bar chahiye, VIP
          lounge chahiye, backstage chahiye."</i> Gate par khada <b style={{ color: C.amber }}>bouncer (= SCC admission
          controller)</b> guest ki kalaai dekhta hai — kaunsa <b style={{ color: C.amber }}>wristband (= SCC)</b> bandha
          hai?
          <br />
          <br />
          🟢 <b>General band (restricted-v2)</b> → sirf dance floor. Root nahi, host nahi, kuch extra nahi. <br />
          🟡 <b>VIP band (anyuid)</b> → thoda extra, root UID chalega. <br />
          🔴 <b>All-access laminate (privileged)</b> → kahin bhi ghus jao, backstage bhi.
          <br />
          <br />
          Rule simple hai: <b>guest ki demand agar uske wristband se zyada ho → entry DENIED.</b> Ye bilkul wahi hotel
          wristband wali baat hai jo humne pehle dekhi thi — band ka rang decide karta hai kahan tak jaa sakte ho.
        </Analogy>

        <p>
          Ek doosra angle, tumhare purane <b style={{ color: C.text }}>apartment building</b> wale frame se: SCC building
          ka <b>security guard + access-card</b> hai. General card se sirf apna floor. Master card (privileged) se
          basement, terrace, control room — sab. Guard har baar card swipe check karta hai, uske bina lift move nahi hoti.
        </p>
      </Section>

      {/* 3 — BUILT-IN SCCs */}
      <Section id="builtin" kicker="03 · The Wristbands" title="OpenShift ke built-in SCCs (spectrum)">
        <p>
          OpenShift kai ready-made SCCs deta hai — ek taraf sabse tight, doosri taraf sabse loose. <K>oc get scc</K>{" "}
          chalao toh poori list dikhti hai. Ye raha unka power-spectrum:
        </p>
        <Spectrum />
        <p>
          Do naam yaad rakhne layak: <b style={{ color: C.green }}>restricted-v2</b> — jo har naye pod ko by default
          milta hai (isliye tumhare bahut se images "as-is" chalte hue permission-denied dete hain), aur{" "}
          <b style={{ color: C.red }}>privileged</b> — jo sirf system-level trusted pods (monitoring, storage, network
          operators) ko milna chahiye, kabhi aam app ko nahi.
        </p>
        <TerminalBlock
          lines={[
            { p: "$", t: "oc get scc" },
            { t: "NAME                PRIV    CAPS         RUNASUSER", c: C.faint },
            { t: "anyuid              false   <no caps>    RunAsAny", c: C.amber },
            { t: "hostnetwork-v2      false   <no caps>    MustRunAsRange", c: C.purple },
            { t: "nonroot-v2          false   <no caps>    MustRunAsNonRoot", c: C.cyan },
            { t: "privileged          true    <all>        RunAsAny", c: C.red },
            { t: "restricted-v2       false   <no caps>    MustRunAsRange", c: C.green },
          ]}
        />
        <p style={{ fontSize: 14, color: C.faint }}>
          <b style={{ color: C.text }}>RUNASUSER</b> column pe gaur karo — yahi asli farq hai. <K>MustRunAsRange</K> =
          non-root, UID namespace ki fixed range se milega. <K>MustRunAsNonRoot</K> = koi bhi UID chalega bas 0 nahi.{" "}
          <K>RunAsAny</K> = kuch bhi, root bhi.
        </p>
      </Section>

      {/* 4 — HOW IT DECIDES (SIMULATOR) */}
      <Section id="how" kicker="04 · The Gate" title="Faisla kaise hota hai — live simulator">
        <p>
          Jab tum <K>oc apply</K> karte ho, request seedha pod nahi banati — pehle API server ki pipeline se guzarti hai.
          Us pipeline me ek stop hai <b style={{ color: C.red }}>SCC Admission</b>. Yahin bouncer khada hai. Neeche khud
          khelo: pod ki demand toggle karo (kya maang raha hai), ServiceAccount ka SCC choose karo (kaunsa wristband),
          phir <K>oc apply</K> dabao aur faisla animate hote dekho.
        </p>

        <Simulator />

        <p style={{ marginTop: 20 }}>
          Try karo: pod me <b style={{ color: C.red }}>root ON</b> rakho aur SCC <b style={{ color: C.green }}>restricted-v2</b> —{" "}
          <b style={{ color: C.red }}>DENIED</b>. Ab wahi pod, SCC ko <b style={{ color: C.amber }}>anyuid</b> kar do —{" "}
          <b style={{ color: C.green }}>ADMITTED</b>. Yehi asli maze ki baat hai: pod nahi badla, sirf uski{" "}
          <b>ijaazat</b> badli.
        </p>
      </Section>

      {/* 5 — MANAGEMENT */}
      <Section id="manage" kicker="05 · Wiring" title="SCC manage kaise hota hai? (yeh crux hai)">
        <p>
          Sabse important galatfehmi yahin door karni hai:{" "}
          <b style={{ color: C.text }}>SCC pod ko directly assign nahi hota.</b> SCC{" "}
          <b style={{ color: C.amber }}>ServiceAccount</b> ko diya jaata hai (RBAC ke through). Aur har pod kisi na kisi
          ServiceAccount ke naam par chalta hai. Toh chain aisi banti hai:
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            background: C.panel,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 18,
            margin: "20px 0",
            fontFamily: mono,
            fontSize: 13,
          }}
        >
          <span style={chip(C.cyan)}><Boxes size={15} /> Pod</span>
          <ChevronRight size={15} color={C.faint} />
          <span style={chip(C.amber)}><User size={15} /> ServiceAccount</span>
          <ChevronRight size={15} color={C.faint} />
          <span style={chip(C.purple)}><KeyRound size={15} /> RoleBinding</span>
          <ChevronRight size={15} color={C.faint} />
          <span style={chip(C.green)}><Shield size={15} /> SCC</span>
        </div>

        <p>Command level pe teen cheezein baar-baar aayengi:</p>
        <TerminalBlock
          lines={[
            { t: "# saare SCCs dekho", c: C.faint },
            { p: "$", t: "oc get scc" },
            { t: "", t2: "" },
            { t: "# ek SCC ko andar se samjho (kaunsi field kya keh rahi)", c: C.faint },
            { p: "$", t: "oc describe scc restricted-v2" },
            { t: "", t2: "" },
            { t: "# ek serviceaccount ko SCC do (-z = serviceaccount flag)", c: C.faint },
            { p: "$", t: "oc adm policy add-scc-to-user anyuid -z billing-sa -n billing" },
          ]}
        />
        <p style={{ fontSize: 14, color: C.faint }}>
          <K>-z</K> ka matlab hai "ye user nahi, serviceaccount hai". Bina <K>-z</K> ke woh command human user ko SCC
          deta hai — jo aksar galti hoti hai. Interview me ye chota-sa flag pooch liya jaata hai.
        </p>
      </Section>

      {/* 6 — USE CASE */}
      <Section id="usecase" kicker="06 · Real Scenario · lab.ocp.local" title="Use-case: jab SCC actually chahiye pada">
        <p>
          <b style={{ color: C.text }}>Situation:</b> Tumhare paas ek vendor ka off-the-shelf image aaya —{" "}
          <K>legacy-billing-app:1.4</K>. Ye purani app hai jo <b>root (UID 0)</b> ke roop me chalti hai kyunki startup pe{" "}
          <K>/var/lib/billing</K> me files likhti hai aur <b>port 80</b> bind karti hai. Tum ise{" "}
          <K>billing</K> namespace me deploy karte ho — bina kuch soche.
        </p>

        <div style={{ fontFamily: mono, fontSize: 12, color: C.red, margin: "18px 0 6px", letterSpacing: 1 }}>
          STEP 1 — deploy karte hi crash 💥
        </div>
        <TerminalBlock
          lines={[
            { p: "$", t: "oc new-app --image=registry.vendor.io/legacy-billing-app:1.4 -n billing" },
            { p: "$", t: "oc get pods -n billing" },
            { t: "NAME                     READY   STATUS             RESTARTS", c: C.faint },
            { t: "legacy-billing-xyz       0/1     CrashLoopBackOff   4", c: C.red },
            { p: "$", t: "oc logs legacy-billing-xyz -n billing" },
            { t: "mkdir: cannot create '/var/lib/billing': Permission denied", c: C.red },
            { t: "nginx: bind() to 0.0.0.0:80 failed (13: Permission denied)", c: C.red },
          ]}
        />
        <p>
          Kyun? Kyunki default <b style={{ color: C.green }}>restricted-v2</b> ne pod ko namespace ki UID range se ek{" "}
          <b>random non-root UID</b> (jaise 1000670000) de diya. App root expect kar rahi thi — isliye na directory ban
          rahi na port 80 (jo &lt;1024 privileged port hai) bind ho raha. Bouncer ne app ko root wristband diya hi nahi.
        </p>

        <div style={{ fontFamily: mono, fontSize: 12, color: C.amber, margin: "22px 0 6px", letterSpacing: 1 }}>
          STEP 2 — diagnose: pod ko kaunsa SCC mila?
        </div>
        <TerminalBlock
          lines={[
            { p: "$", t: "oc get pod legacy-billing-xyz -n billing -o yaml | grep scc" },
            { t: 'openshift.io/scc: restricted-v2', c: C.green },
          ]}
        />
        <p>
          Confirm ho gaya — pod restricted-v2 ke andar hai, aur app ki demand (root + port 80) us wristband se zyada hai.
        </p>

        <div style={{ fontFamily: mono, fontSize: 12, color: C.green, margin: "22px 0 6px", letterSpacing: 1 }}>
          STEP 3 — sahi tareeka: dedicated SA banao, phir SCC do
        </div>
        <TerminalBlock
          lines={[
            { t: "# 1) is app ke liye alag serviceaccount", c: C.faint },
            { p: "$", t: "oc create sa billing-sa -n billing" },
            { t: "", t2: "" },
            { t: "# 2) us SA ko anyuid do (root allow ho jayega)", c: C.faint },
            { p: "$", t: "oc adm policy add-scc-to-user anyuid -z billing-sa -n billing" },
            { t: "", t2: "" },
            { t: "# 3) deployment ko us SA pe chalao", c: C.faint },
            { p: "$", t: "oc set serviceaccount deploy/legacy-billing billing-sa -n billing" },
            { p: "$", t: "oc get pods -n billing" },
            { t: "legacy-billing-abc       1/1     Running   0", c: C.green },
          ]}
        />
        <p>
          Ab pod Running hai. Tumne image ko haath nahi lagaya — sirf uski <b>ijaazat</b> badli, aur wo bhi{" "}
          <b style={{ color: C.text }}>ek dedicated ServiceAccount</b> par, poore namespace par nahi. Yahi discipline hai.
        </p>

        <Warn>
          <b>Ruko — anyuid muft nahi hai.</b> Tumne app ko root UID de diya. Agar is image me kal koi vulnerability
          nikli, to root container escape ka woh pura khatra wapas aa gaya jo humne section 01 me dekha tha.{" "}
          <b>anyuid ko kabhi cluster-wide ya default recommendation mat samjho</b> — ye aakhri sahara hai, pehla nahi.
        </Warn>

        <div style={{ fontFamily: mono, fontSize: 12, color: C.cyan, margin: "22px 0 6px", letterSpacing: 1 }}>
          STEP 4 — behtar raaste (production mindset)
        </div>
        <p style={{ marginTop: 4 }}>
          Ek architect anyuid par rukta nahi. Ghatte hue risk ke order me options:
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "14px 0" }}>
          {[
            { t: "Image ko non-root banao", d: "Dockerfile me USER 1001, port 80 → 8080, writable dir /tmp ya emptyDir. Sabse saaf fix — restricted-v2 par hi chal jayega, koi extra SCC nahi.", c: C.green },
            { t: "Custom minimal SCC", d: "anyuid ki poori chhoot mat do. Ek SCC banao jo sirf root UID allow kare aur baaki sab (privileged, host, caps) block rakhe. 'Least privilege' — sirf utni power jitni chahiye.", c: C.cyan },
            { t: "anyuid on dedicated SA", d: "Agar image abhi nahi badal sakte to yahi, par isolated SA + namespace pe, taaki blast-radius chhota rahe.", c: C.amber },
          ].map((o) => (
            <div key={o.t} style={{ background: C.panel, border: `1px solid ${C.border}`, borderLeft: `3px solid ${o.c}`, borderRadius: 9, padding: "13px 15px" }}>
              <div style={{ fontFamily: mono, fontWeight: 700, color: o.c, fontSize: 13.5, marginBottom: 4 }}>{o.t}</div>
              <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.6 }}>{o.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 7 — SUMMARY */}
      <Section id="summary" kicker="07 · One-page" title="Ek page ka summary">
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: "6px 20px" }}>
          {[
            ["Kyun", "Container root ho aur escape bug ho to host compromise. SCC pod banne se pehle security demands ko rules ke against jaanchta hai."],
            ["Kya", "SCC = ek rulebook: root allowed? UID range? privileged? host net/disk? capabilities? Admission controller ye enforce karta hai."],
            ["Spectrum", "restricted-v2 (default, tightest) → nonroot-v2 → anyuid → hostnetwork-v2 → hostaccess → privileged (loosest)."],
            ["Management", "SCC pod ko nahi, ServiceAccount ko milta hai. Chain: Pod → SA → RoleBinding → SCC."],
            ["Command", "oc adm policy add-scc-to-user <scc> -z <sa> -n <ns>  (−z = serviceaccount)."],
            ["Golden rule", "Least privilege. anyuid/privileged aakhri sahara. Behtar: image non-root banao ya custom minimal SCC."],
          ].map(([k, v], i, a) => (
            <div key={k} style={{ display: "flex", gap: 16, padding: "14px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ fontFamily: mono, color: C.red, fontSize: 13, fontWeight: 700, minWidth: 96, flexShrink: 0 }}>{k}</div>
              <div style={{ color: C.muted, fontSize: 14.5, lineHeight: 1.6 }}>{v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8 — CHEAT SHEET */}
      <Section id="cheat" kicker="08 · Muscle Memory" title="Revision cheat-sheet (oc commands)">
        <TerminalBlock
          lines={[
            { t: "# discovery", c: C.faint },
            { p: "$", t: "oc get scc                              # list all SCCs" },
            { p: "$", t: "oc describe scc restricted-v2           # ek SCC ke rules" },
            { p: "$", t: "oc get pod <p> -o yaml | grep scc       # pod ko kaunsa SCC mila" },
            { t: "", t2: "" },
            { t: "# assign / remove", c: C.faint },
            { p: "$", t: "oc adm policy add-scc-to-user anyuid -z <sa> -n <ns>" },
            { p: "$", t: "oc adm policy remove-scc-from-user anyuid -z <sa> -n <ns>" },
            { p: "$", t: "oc adm policy who-can use scc anyuid    # kaun use kar sakta" },
            { t: "", t2: "" },
            { t: "# wiring", c: C.faint },
            { p: "$", t: "oc create sa <sa> -n <ns>" },
            { p: "$", t: "oc set serviceaccount deploy/<d> <sa> -n <ns>" },
          ]}
        />
      </Section>

      {/* 9 — INTERVIEW */}
      <Section id="interview" kicker="09 · Interview Prep" title="Interview questions">
        <QA q="SCC aur Kubernetes PodSecurityPolicy/PSA me farq kya hai?">
          SCC OpenShift ka apna, purana, zyada granular mechanism hai jo default se ON rehta hai. PSP Kubernetes ka
          equivalent tha (ab deprecated), uski jagah <K>Pod Security Admission</K> aaya. Practically OpenShift clusters
          me SCC hi enforce hota hai, aur ye service-account level par bind hota hai.
        </QA>
        <QA q="Ek pod ko multiple SCCs eligible ho to kaunsa lagta hai?">
          OpenShift SCCs ko priority aur restrictiveness ke hisaab se sort karta hai, phir pehla SCC jo pod ki demands
          ko admit kar de wahi use hota hai. Isliye pod ko usually sabse restrictive matching SCC milta hai jo uski
          zarooraton ko satisfy kare.
        </QA>
        <QA q="restricted-v2 ne mere root-image ko random UID kyun diya?">
          restricted-v2 ka runAsUser strategy <K>MustRunAsRange</K> hai — ye namespace ke annotate kiye gaye UID range
          se ek non-root UID assign karta hai. Isliye root expect karti image ko wo UID nahi milta aur permission-denied
          errors aate hain.
        </QA>
        <QA q="anyuid dene ka security trade-off kya hai?">
          anyuid pod ko UID 0 (root) chalane deta hai. Agar image me container-escape vulnerability ho to root escape ka
          risk khul jaata hai. Isliye ise dedicated SA + isolated namespace pe do, cluster-wide kabhi nahi, aur behtar
          ho to image ko non-root banao.
        </QA>
        <QA q="-z flag kyun important hai?">
          <K>-z</K> batata hai ki SCC ek ServiceAccount ko de rahe ho, human user ko nahi. Kyunki pods SA ke naam par
          chalte hain, SCC hamesha SA pe bind hona chahiye. Bina -z ke tum galti se user ko de doge, pod ko fayda nahi
          hoga.
        </QA>
      </Section>

      {/* 10 — TROUBLESHOOTING */}
      <Section id="trouble" kicker="10 · On-call" title="Troubleshooting questions">
        <QA q="Pod CrashLoopBackOff, logs me 'Permission denied' — pehla kadam?">
          <K>oc get pod &lt;p&gt; -o yaml | grep scc</K> se dekho kaunsa SCC laga. Aksar restricted-v2 hoga aur app root
          ya privileged port maang rahi hogi. Fix: image non-root banao, ya dedicated SA ko sahi SCC do.
        </QA>
        <QA q="Pod 'unable to validate against any security context constraint' se create hi nahi ho raha?">
          Iska matlab pod ke SA ke paas koi bhi aisa SCC nahi jo uski securityContext demands (jaise privileged/hostPath)
          allow kare. Zaroori SCC us SA ko <K>add-scc-to-user</K> se do — par pehle check karo demand actually chahiye ya
          nahi.
        </QA>
        <QA q="anyuid diya phir bhi pod fail — kyun?">
          Common galtiyan: (1) SCC user ko diya, SA ko nahi (−z bhool gaye). (2) Deployment abhi bhi default SA pe chal
          raha, custom SA set nahi kiya (<K>oc set serviceaccount</K>). (3) App root ke saath-saath privileged/hostNet
          bhi maang rahi — anyuid ye allow nahi karta, alag SCC chahiye.
        </QA>
      </Section>

      {/* 11 — SCENARIO */}
      <Section id="scenario" kicker="11 · Think Like Architect" title="Scenario-based questions">
        <QA q="Team ek monitoring agent deploy karna chahti hai jo host ke network interfaces padhta hai. Konsa SCC?">
          Ye host-network access maang raha hai, root nahi. Toh <K>hostnetwork-v2</K> (ya minimal custom SCC jo sirf
          hostNetwork allow kare) sahi hai — privileged dena over-kill aur risky hoga. Least privilege: sirf host-network,
          baaki sab block.
        </QA>
        <QA q="Vendor ne kaha 'privileged: true chahiye' — tum architect ho, kya karoge?">
          Pehle challenge karo: exactly kaunsi capability chahiye? Aksar vendors "privileged" keh dete hain jabki asal me
          ek-do capabilities (jaise NET_ADMIN) ya hostPath chahiye. Custom SCC banao jo sirf wahi de. privileged sirf tab
          jab genuinely har cheez chahiye ho, aur woh bhi audit + isolated namespace ke saath.
        </QA>
        <QA q="Ek namespace me 5 alag apps, sabko alag security needs. Design?">
          Har app ka apna ServiceAccount banao aur har SA ko sirf uski zaroorat wala SCC do. Ek blanket anyuid namespace
          ke default SA pe mat lagao — warna woh chhoot har pod ko mil jayegi. Per-SA scoping = chhota blast-radius +
          clear audit trail.
        </QA>

        <div style={{ marginTop: 34, textAlign: "center", padding: 26, background: "linear-gradient(180deg,#0d0608,#080b12)", border: `1px solid ${C.border}`, borderRadius: 14 }}>
          <Shield size={30} color={C.red} style={{ marginBottom: 10 }} />
          <div style={{ fontFamily: mono, color: C.text, fontSize: 15, fontWeight: 700 }}>
            SCC module complete <span className="blink" style={{ color: C.green }}>█</span>
          </div>
          <div style={{ color: C.faint, fontSize: 13.5, marginTop: 8, maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            Yaad rakho: SCC ka dil "least privilege" hai — pod ko sirf utni power do jitni bilkul zaroori hai, ek
            byte zyada nahi. Agla topic ready ho to bolo <b style={{ color: C.text }}>"Next Module"</b>.
          </div>
        </div>
      </Section>

      <div style={{ height: 40 }} />
    </div>
  );
}

const chip = (c) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: `1px solid ${c}66`,
  color: c,
  borderRadius: 8,
  padding: "7px 12px",
  background: "#0a0f17",
});